import React, { FC, useRef, useState, useMemo } from 'react';

interface VoiceMessageProps {
  audioData: string;
  isOwn: boolean;
}

const VoiceMessage: FC<VoiceMessageProps> = ({ audioData, isOwn }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [dur, setDur] = useState<number | null>(null);

  // 26 bars — deterministic heights seeded from audio data length
  const bars = useMemo<number[]>(() => {
    const s =
      audioData.length +
      (audioData.charCodeAt(30) || 0) +
      (audioData.charCodeAt(60) || 0);
    return Array.from({ length: 26 }, (_, i) => {
      const a = Math.abs(Math.sin(i * 0.71 + s * 0.0019));
      const b = Math.abs(Math.sin(i * 1.93 + s * 0.0037));
      return Math.max(0.1, Math.min(0.95, a * 0.55 + b * 0.45));
    });
  }, [audioData.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtDur = (t: number | null): string => {
    if (t == null || !isFinite(t) || isNaN(t) || t < 0) return '0:00';
    return `${Math.floor(t / 60)}:${Math.floor(t % 60).toString().padStart(2, '0')}`;
  };

  const togglePlay = (): void => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) a.pause();
    else a.play().catch(() => {});
  };

  const handleWaveformClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    const a = audioRef.current;
    if (!a || !isFinite(a.duration) || a.duration === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    a.currentTime = ratio * a.duration;
    setProgress(ratio);
  };

  const displayedTime = playing && dur !== null ? progress * dur : (dur ?? 0);

  return (
    <div className={`voice-msg${isOwn ? ' own' : ''}`}>
      <audio
        ref={audioRef}
        src={audioData}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setProgress(0); }}
        onTimeUpdate={() => {
          const a = audioRef.current;
          if (a && isFinite(a.duration) && a.duration > 0)
            setProgress(a.currentTime / a.duration);
        }}
        onLoadedMetadata={() => {
          const a = audioRef.current;
          if (a && isFinite(a.duration)) setDur(a.duration);
        }}
      />
      <button
        className="voice-play-btn"
        onClick={togglePlay}
        aria-label={playing ? 'Pause' : 'Play'}
      >
        {playing ? '⏸' : '▶'}
      </button>
      <div className="waveform" onClick={handleWaveformClick}>
        {bars.map((h, i) => (
          <div
            key={i}
            className={`wf-bar${i / bars.length < progress ? ' played' : ''}`}
            style={{ height: `${Math.round(h * 100)}%` }}
          />
        ))}
      </div>
      <span className="voice-dur">{fmtDur(displayedTime)}</span>
    </div>
  );
};

export default VoiceMessage;
